'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, useDocumentInfo, useFormFields } from '@payloadcms/ui'
import { relationId } from '../../lib/relations'
import { errMessage } from '../../lib/errors'

/**
 * ANTEPRIMA DELLE EMAIL AGLI ISCRITTI, dentro le impostazioni del sito.
 *
 * Rende l'email vera chiedendola al server (POST /api/newsletter-preview) e la mostra in un
 * iframe. Non ricostruisce il markup in React: se lo facesse, l'anteprima somiglierebbe all'email
 * invece di essere l'email, e la prima differenza fra le due comparirebbe in casella di un lettore.
 *
 * I parametri che manda sono quelli CORRENTI del form, non quelli salvati: il senso di un'anteprima
 * è decidere prima di salvare. Per la stessa ragione si aggiorna da sé quando cambi l'aspetto o
 * l'altezza del logo, con mezzo secondo di attesa per non chiamare il server a ogni tasto.
 *
 * L'iframe è `sandbox` senza permessi: il documento dentro è HTML di email, senza script, e non ha
 * nessuna ragione di poter navigare o aprire finestre dalla pagina di amministrazione.
 */

const ENDPOINT = '/api/newsletter-preview'

const KINDS = [
  { value: 'conferma', label: 'Conferma iscrizione' },
  { value: 'benvenuto', label: 'Benvenuto' },
  { value: 'articolo', label: 'Articolo nuovo' },
  { value: 'gia-iscritto', label: 'Era già iscritto' },
] as const

const styles: Record<string, React.CSSProperties> = {
  wrap: { marginTop: '1.5rem', borderTop: '1px solid var(--theme-elevation-150)', paddingTop: '1.25rem' },
  head: { alignItems: 'baseline', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'space-between' },
  title: { fontSize: '1rem', fontWeight: 600, margin: 0 },
  row: { display: 'flex', flexWrap: 'wrap', gap: '0.4rem', margin: '0.75rem 0' },
  subject: {
    background: 'var(--theme-elevation-50)',
    border: '1px solid var(--theme-elevation-150)',
    borderRadius: 'var(--style-radius-s)',
    fontSize: '0.85rem',
    margin: '0 0 0.6rem',
    padding: '0.5rem 0.7rem',
  },
  note: { color: 'var(--theme-elevation-600)', fontSize: '0.78rem', margin: '0 0 0.6rem' },
  frame: {
    background: '#f4f4f5',
    border: '1px solid var(--theme-elevation-150)',
    borderRadius: 'var(--style-radius-s)',
    display: 'block',
    height: '620px',
    margin: '0 auto',
    width: '100%',
  },
}

export default function NewsletterEmailPreview() {
  const { id: settingsId } = useDocumentInfo()

  // `unknown`: il valore di un campo relazione arriva come id nudo o come documento popolato,
  // e dichiararlo `number | string` era una bugia che il narrowing qui sotto doveva aggirare.
  const tenantField = useFormFields(([fields]) => fields?.tenant?.value as unknown)
  const template = useFormFields(([fields]) => fields?.['newsletter.emailTemplate']?.value as string | undefined)
  const logoHeight = useFormFields(([fields]) => fields?.['newsletter.emailLogoHeight']?.value as number | undefined)
  const promise = useFormFields(([fields]) => fields?.['newsletter.formDescription']?.value as string | undefined)

  const [kind, setKind] = useState<string>('conferma')
  const [width, setWidth] = useState<'desktop' | 'telefono'>('desktop')
  const [data, setData] = useState<{ html: string; subject: string; note?: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Il sito: dal campo del form se c'è — quello si LEGGE a ogni render, non c'è niente da
  // conservare in stato — altrimenti dal documento salvato, che va chiesto al server. Nel
  // pannello multi-tenant il campo può essere nascosto, e senza il sito non si rende niente.
  const fieldTenant = relationId(tenantField)
  const [savedTenant, setSavedTenant] = useState<number | null>(null)
  const tenantId = fieldTenant != null && fieldTenant !== '' ? Number(fieldTenant) : savedTenant

  useEffect(() => {
    if (fieldTenant || !settingsId) return
    let alive = true
    void (async () => {
      try {
        const res = await fetch(`/api/integrationSettings/${settingsId}?depth=0`, { credentials: 'include' })
        const doc = (await res.json().catch(() => null)) as { tenant?: unknown } | null
        const t = relationId(doc?.tenant)
        if (alive && t != null) setSavedTenant(Number(t))
      } catch {
        /* si resta senza sito: sotto compare il messaggio */
      }
    })()
    return () => {
      alive = false
    }
  }, [fieldTenant, settingsId])

  const load = useCallback(async () => {
    if (!tenantId) return
    setBusy(true)
    setError(null)
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tenantId, kind, template, logoHeight, promise }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(json?.error || `errore ${res.status}`)
        setData(null)
        return
      }
      setData({ html: json.html, subject: json.subject, note: json.note })
    } catch (e: unknown) {
      setError(errMessage(e) || 'anteprima non disponibile')
    } finally {
      setBusy(false)
    }
  }, [tenantId, kind, template, logoHeight, promise])

  // Mezzo secondo di attesa: l'altezza del logo si cambia con le frecce, e senza questo ogni
  // pressione sarebbe una richiesta.
  useEffect(() => {
    const t = setTimeout(() => void load(), 500)
    return () => clearTimeout(t)
  }, [load])

  const frameStyle = useMemo(
    () => ({ ...styles.frame, maxWidth: width === 'telefono' ? '400px' : '100%' }),
    [width],
  )

  return (
    <div style={styles.wrap}>
      <div style={styles.head}>
        <h4 style={styles.title}>Anteprima delle email</h4>
        <span style={{ fontSize: '0.78rem', color: 'var(--theme-elevation-600)' }}>
          {busy ? 'aggiorno…' : 'segue i campi qui sopra, anche prima di salvare'}
        </span>
      </div>

      <div style={styles.row}>
        {KINDS.map((k) => (
          <Button
            key={k.value}
            buttonStyle={kind === k.value ? 'primary' : 'secondary'}
            size="small"
            onClick={() => setKind(k.value)}
          >
            {k.label}
          </Button>
        ))}
        <span style={{ flex: 1 }} />
        <Button buttonStyle={width === 'desktop' ? 'primary' : 'secondary'} size="small" onClick={() => setWidth('desktop')}>
          Schermo
        </Button>
        <Button buttonStyle={width === 'telefono' ? 'primary' : 'secondary'} size="small" onClick={() => setWidth('telefono')}>
          Telefono
        </Button>
      </div>

      {!tenantId ? (
        <p style={styles.note}>Salva le impostazioni una volta: l&apos;anteprima ha bisogno di sapere di quale sito sono.</p>
      ) : error ? (
        <p style={{ ...styles.note, color: 'var(--theme-error-500)' }}>Anteprima non disponibile: {error}</p>
      ) : (
        <>
          {data?.subject ? (
            <p style={styles.subject}>
              <strong>Oggetto:</strong> {data.subject}
            </p>
          ) : null}
          {data?.note ? <p style={styles.note}>{data.note}</p> : null}
          <iframe
            title="Anteprima email"
            style={frameStyle}
            sandbox=""
            srcDoc={data?.html ?? '<p style="font-family:sans-serif;padding:24px;color:#71717a">Carico…</p>'}
          />
        </>
      )}
    </div>
  )
}
